import twemoji from "twemoji";
import EasyMDE from "easymde";
import Lightense from "lightense-images";

import { isCommunicationForm, isMobile } from "./common/utils";
import {
    createFileInput,
    createMarkdownEditor,
    handleFormSubmissionShortcuts,
    imageUploadOptions,
} from "./common/markdown-editor";
import { getCollapsedCommentThreadsSet } from "./common/comments";

const INITIAL_SYNC_DELAY = 50;

const App = {
    onCreate() {
        this.initializeThemeSwitcher();
        this.addTargetBlankToExternalLinks();
    },
    onMount() {
        this.initializeImageZoom();
        this.initializeEmojiForPoorPeople();
        this.initializeWorkoutAsyncUploads();
        this.blockCommunicationFormsResubmit();
        this.restoreCommentThreadsState();

        const registeredEditors = this.initializeMarkdownEditor();

        setTimeout(function () {
            registeredEditors.forEach((editor) => {
                // textarea value after navigation might be restored after codemirror inited
                if (editor.element.value && !editor.codemirror.getValue()) {
                    editor.codemirror.setValue(editor.element.value);
                }
            });
        }, INITIAL_SYNC_DELAY);
    },
    initializeWorkoutAsyncUploads() {
        const form = document.querySelector('.compose-form[data-workout-upload-form="1"]');
        if (!form || !window.imageUploadUrl || !window.imageUploadCode) {
            return;
        }

        const videoFileInput = form.querySelector('input[name="video_file"]');
        const videoUrlInput = form.querySelector('input[name="video_url"]');
        const videoStatus = form.querySelector('[data-upload-status="video"]');

        const posterFileInput = form.querySelector('input[name="poster_file"]');
        const posterUrlInput = form.querySelector('input[name="poster_url"]');
        const posterStatus = form.querySelector('[data-upload-status="poster"]');

        const submitButtons = [...form.querySelectorAll('button[type="submit"]')];
        let activeUploads = 0;

        function setUploadState(isUploading) {
            if (isUploading) {
                activeUploads += 1;
            } else {
                activeUploads = Math.max(0, activeUploads - 1);
            }

            const hasUploading = activeUploads > 0;
            form.dataset.uploading = hasUploading ? "1" : "0";
            submitButtons.forEach((button) => {
                button.disabled = hasUploading;
            });
        }

        function setStatus(statusElement, text, isError = false) {
            if (!statusElement) {
                return;
            }
            statusElement.textContent = text;
            statusElement.style.color = isError ? "#d34b4b" : "";
        }

        async function uploadSelectedFile(fileInput, urlInput, statusElement, mediaLabel) {
            const file = fileInput?.files?.[0];
            if (!file) {
                return;
            }

            setUploadState(true);
            setStatus(statusElement, `Загрузка ${mediaLabel}: 0%`);

            try {
                const uploadedUrl = await uploadFileToMediaService(file, (progress) => {
                    setStatus(statusElement, `Загрузка ${mediaLabel}: ${progress}%`);
                });

                urlInput.value = uploadedUrl;
                fileInput.value = "";
                setStatus(statusElement, `${mediaLabel} загружено`);
            } catch (error) {
                console.error(error);
                setStatus(statusElement, `Ошибка загрузки: ${mediaLabel}`, true);
            } finally {
                setUploadState(false);
            }
        }

        form.addEventListener("submit", (event) => {
            if (form.dataset.uploading === "1") {
                event.preventDefault();
            }
        });

        if (videoFileInput && videoUrlInput) {
            videoFileInput.addEventListener("change", () => {
                uploadSelectedFile(videoFileInput, videoUrlInput, videoStatus, "видео");
            });
        }

        if (posterFileInput && posterUrlInput) {
            posterFileInput.addEventListener("change", () => {
                uploadSelectedFile(posterFileInput, posterUrlInput, posterStatus, "превью");
            });
        }
    },
    initializeEmojiForPoorPeople() {
        const isApple = /iPad|iPhone|iPod|OS X/.test(navigator.userAgent) && !window.MSStream;
        if (!isApple) {
            document.body = twemoji.parse(document.body, {
                base: "https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/",
            });
        }
    },
    initializeThemeSwitcher() {
        const mediaQueryList = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)");

        const setFaviconHref = (e) => {
            const svgFavicon = document.querySelector('link[type="image/svg+xml"]');
            const isDark = e.matches;
            svgFavicon.href = isDark ? "/static/images/favicon/favicon-dark.svg" : "/static/images/favicon/favicon.svg";
        };

        setFaviconHref(mediaQueryList);
        mediaQueryList.addListener(setFaviconHref);
    },
    initializeMarkdownEditor() {
        const fullMarkdownEditors = [...document.querySelectorAll(".markdown-editor-full")].reduce(
            (editors, element) => {
                const fileInputEl = createFileInput({ allowedTypes: imageUploadOptions.allowedTypes });
                const editor = createMarkdownEditor(element, {
                    autosave: {
                        enabled: false,
                    },
                    hideIcons: ["preview", "side-by-side", "fullscreen", "guide"],
                    showIcons: ["heading-2", "code"],
                    toolbar: [
                        {
                            name: "bold",
                            action: EasyMDE.toggleBold,
                            className: "fa fa-bold",
                            title: "Bold",
                        },
                        {
                            name: "italic",
                            action: EasyMDE.toggleItalic,
                            className: "fa fa-italic",
                            title: "Italic",
                        },
                        {
                            name: "header",
                            action: EasyMDE.toggleHeadingSmaller,
                            className: "fas fa-heading",
                            title: "Heading",
                        },
                        {
                            name: "quote",
                            action: EasyMDE.toggleBlockquote,
                            className: "fas fa-quote-right",
                            title: "Quote",
                        },
                        "|",
                        {
                            name: "list",
                            action: EasyMDE.toggleUnorderedList,
                            className: "fas fa-list",
                            title: "List",
                        },
                        {
                            name: "url",
                            action: EasyMDE.drawLink,
                            className: "fas fa-link",
                            title: "Insert URL",
                        },
                        {
                            name: "image",
                            action: EasyMDE.drawImage,
                            className: "fas fa-image",
                            title: "Insert an image",
                        },
                        {
                            name: "upload-file",
                            action: () => {
                                fileInputEl.click();
                            },
                            className: "fa fa-paperclip",
                            title: "Upload image",
                        },
                        {
                            name: "code",
                            action: EasyMDE.toggleCodeBlock,
                            className: "fas fa-code",
                            title: "Insert code",
                        },
                    ],
                });

                editor.element.form.addEventListener("keydown", handleFormSubmissionShortcuts);
                inlineAttachment.editors.codemirror4.attach(editor.codemirror, { ...imageUploadOptions, fileInputEl });

                return [...editors, editor];
            },
            []
        );

        return fullMarkdownEditors;
    },
    addTargetBlankToExternalLinks() {
        let internal = location.host.replace("www.", "");
        internal = new RegExp(internal, "i");

        const links = [...document.getElementsByTagName("a")];
        links.forEach((link) => {
            if (internal.test(link.host)) return;

            link.setAttribute("target", "_blank");
        });
    },
    initializeImageZoom() {
        Lightense(document.querySelectorAll(".text-body figure img"), {
            time: 100,
            padding: 40,
            offset: 40,
            keyboard: true,
            cubicBezier: "cubic-bezier(.2, 0, .1, 1)",
            background: "rgba(0, 0, 0, .4)",
            zIndex: 1e6,
        });
    },
    blockCommunicationFormsResubmit() {
        [...document.querySelectorAll("form")].filter(isCommunicationForm).forEach((form) => {
            form.addEventListener("submit", () => {
                const submitButton = form.querySelector('button[type="submit"]');

                if (!submitButton) {
                    return;
                }

                submitButton.setAttribute("disabled", true);
            });
        });
    },
    restoreCommentThreadsState() {
        const comments = document.querySelectorAll(".reply, .comment");
        const collapsedSet = getCollapsedCommentThreadsSet();
        for (const comment of comments) {
            if (collapsedSet.has(comment.id)) {
                comment.querySelector(".comment-collapse-stub, .reply-collapse-stub").click();
            }
        }
    },
};

function uploadFileToMediaService(file, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();

        formData.append("media", file, file.name || `upload-${Date.now()}`);
        formData.append("code", window.imageUploadCode);

        xhr.open("POST", window.imageUploadUrl, true);
        xhr.setRequestHeader("Accept", "application/json");

        xhr.upload.onprogress = function (event) {
            if (!event.lengthComputable || !onProgress) {
                return;
            }
            const progress = Math.min(100, Math.round((event.loaded / event.total) * 100));
            onProgress(progress);
        };

        xhr.onload = function () {
            if (xhr.status < 200 || xhr.status >= 300) {
                reject(new Error(`Upload failed with status ${xhr.status}`));
                return;
            }

            try {
                const payload = JSON.parse(xhr.responseText);
                const uploadedUrl = payload?.uploaded?.[0];
                if (!uploadedUrl) {
                    reject(new Error("Upload response has no URL"));
                    return;
                }
                resolve(uploadedUrl);
            } catch (error) {
                reject(error);
            }
        };

        xhr.onerror = function () {
            reject(new Error("Upload request failed"));
        };

        xhr.send(formData);
    });
}

export default App;
